import React, {
    Fragment,
    useImperativeHandle,
    useRef,
    useState,
    useCallback,
    useEffect,
} from 'react';
import { Dialog, Transition } from '@headlessui/react';
import cs from 'classnames';
import ThumbIcon from '../../../../public/svgs/thumb.svg';
import TrashIcon from '../../../../public/svgs/trash.svg';
import AlertIcon from '../../../../public/svgs/alert.svg';
import SaveIcon from '../../../../public/svgs/save.svg';
import UpgradeIcon from '../../../../public/svgs/upgrade.svg';
import { Button } from '../../elements/Button';
import s from './Modal.module.css';
import NextImage from 'next/image';
import { Checkbox } from '../../forms/Checkboxes';
import { DangerAlertSVG } from '@components/common/svgs/DangerAlertSVG';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

function IconSelector({
    type = 'success',
}: {
    type?: 'success' | 'warning' | 'info' | 'danger' | 'dangerAlert' | 'upgrade';
}) {
    let Icon = ThumbIcon;

    const iconContainer = cs(s.iroot, {
        'bg-success-background': type === 'success',
        'bg-warning-background': type === 'warning',
        'bg-info-background': type === 'info',
        'bg-danger-background': type === 'danger' || type === 'dangerAlert',
        'bg-danger-background scale-125 mb-8': type === 'upgrade',
    });
    const iconStyle = cs(s.icon, {
        'fill-current text-success': type === 'success',
        'fill-current text-warning': type === 'warning',
        'fill-current text-info': type === 'info',
        'fill-current text-danger': type === 'danger' || type === 'dangerAlert' || type === 'upgrade',
    });

    switch (type) {
        case 'success':
            Icon = SaveIcon;
            break;
        case 'warning':
            Icon = AlertIcon;
            break;
        case 'info':
            Icon = ThumbIcon;
            break;
        case 'danger':
            Icon = TrashIcon;
            break;
        case 'dangerAlert':
            Icon = <DangerAlertSVG />;
            break;
        case 'upgrade':
            Icon = UpgradeIcon;
            break;
        default:
            break;
    }

    return (
        <div className={iconContainer}>
            <NextImage src={Icon} layout="fixed" width={40} height={40} className={iconStyle} />
        </div>
    );
}

export interface ModalProps {
    open: boolean;
    setOpen: (val: boolean) => void;
    body: string;
    type?: 'success' | 'warning' | 'info' | 'danger' | 'dangerAlert';
    confirmText: string;
    onConfirm: () => void;
    onCancel: () => void;
    cancelText?: string;
    title: string;
    advancedOptions?: string[];
    reorderButtons?: boolean;
    advanced?: boolean;
    showChildren?: boolean;
    childComponent?: React.ReactNode;
    textClassname?: string;
    confirmButtonDisabled?: boolean;
}

const Modal = React.forwardRef(
    (
        {
            open,
            setOpen,
            body,
            type = 'success',
            onConfirm,
            onCancel,
            confirmText,
            cancelText,
            title,
            advanced,
            advancedOptions = [],
            reorderButtons = false,
            showChildren = false,
            childComponent,
            textClassname = '',
            confirmButtonDisabled = false,
        }: ModalProps,
        ref: React.ForwardedRef<any>,
    ) => {
        const internalRef = useRef(setOpen);
        const buttonRef = useRef<HTMLButtonElement | null>(null);
        const [valid, setIsValid] = useState(() => {
            if (advancedOptions.length > 0) return false;
            return true;
        });
        const [checked, setChecked] = useState<string[]>([]);

        useEffect(() => {
            !!advancedOptions.length && setChecked([]);
        }, [advancedOptions]);

        const toggleChecked = useCallback(
            (val: string) => {
                setChecked((s) => {
                    if (s.includes(val)) {
                        return s.filter((l) => l !== val);
                    }
                    return [...s, val];
                });
            },
            [setChecked],
        );

        useEffect(() => {
            if (advancedOptions.length > 0) {
                if (advancedOptions.length === checked.length) {
                    setIsValid(true);
                } else {
                    setIsValid(false);
                }
            }
        }, [advancedOptions, checked]);

        useImperativeHandle(
            ref,
            () => ({
                setOpen: (val: boolean) => {
                    internalRef?.current?.(val);
                },
            }),
            [ref],
        );

        const rootContentsClass = cs({
            [s.baseContents]: !advancedOptions?.length,
            [s.advanced]: !!advancedOptions?.length,
        });

        const rootButtonContents = cs({
            [s.buttons]: !advancedOptions?.length,
            [s.buttonsAdvanced]: !!advancedOptions?.length,
        });

        return (
            <Transition.Root show={open} as={Fragment}>
                <Dialog
                    as="div"
                    initialFocus={buttonRef}
                    static
                    className="fixed z-[9999] inset-0 overflow-y-auto"
                    open={open}
                    onClose={setOpen}>
                    <div className={s.modal}>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0">
                            <Dialog.Overlay className={s.overlay} />
                        </Transition.Child>

                        {/* This element is to trick the browser into centering the modal contents. */}
                        <span
                            className="hidden sm:inline-block sm:align-middle sm:h-screen"
                            aria-hidden="true">
                            &#8203;
                        </span>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <div className={s.contents}>
                                <div className="pt-3 pb-5">
                                    <div className="flex w-full items-center justify-end px-3 mb-8">
                                        <FontAwesomeIcon
                                            icon={faXmark}
                                            size="sm"
                                            className="cursor-pointer text-gray-500"
                                            onClick={() => setOpen(false)}
                                        />
                                    </div>
                                    <IconSelector type={type} />
                                    <div className="mt-3 text-center sm:mt-5">
                                        <Dialog.Title
                                            as="h3"
                                            className="text-2xl leading-6 font-medium text-gray-900">
                                            {title}
                                        </Dialog.Title>
                                    </div>
                                </div>
                                <div className={`${rootContentsClass} !px-10`}>
                                    <p
                                        className={`text-base text-gray-500 ${
                                            advanced
                                                ? 'font-[700] text-left mt-5 mb-6'
                                                : 'text-center'
                                        } flex-wrap break-normal whitespace-pre-wrap ${textClassname}`}>
                                        {body}
                                    </p>
                                    <div
                                        className={`flex flex-col gap-y-2 min-w-[500px] white-space: wrap ${textClassname}`}>
                                        {advancedOptions.map((i) => {
                                            return (
                                                <div className="pl-1" key={i}>
                                                    <Checkbox
                                                        variant="secondary"
                                                        key={i}
                                                        name={`advanced-options-${i}`}
                                                        id={`advanced-options-${i}`}
                                                        labelBold=""
                                                        label={i}
                                                        srLabel="advanced-options"
                                                        labelClassName="text-passport"
                                                        wrap
                                                        checked={checked.includes(i)}
                                                        setChecked={() => toggleChecked(i)}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                {showChildren && <div className="mt-3">{childComponent}</div>}
                                <div
                                    className={`${rootButtonContents} ${
                                        type === 'danger' ? 'pt-8' : 'pt-5'
                                    }`}>
                                    {!reorderButtons ? (
                                        <>
                                            <Button
                                                ref={buttonRef}
                                                disabled={!valid || confirmButtonDisabled}
                                                variant={
                                                    type === 'danger' ? 'secondary' : 'primary'
                                                }
                                                size="medium"
                                                className={`justify-center infline-flex block`}
                                                onClick={onConfirm}>
                                                {confirmText}
                                            </Button>
                                            {cancelText && (
                                                <Button
                                                    variant="white"
                                                    size="medium"
                                                    className="justify-center infline-flex block"
                                                    onClick={onCancel}>
                                                    {cancelText}
                                                </Button>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                variant="white"
                                                size="medium"
                                                className="justify-center infline-flex block"
                                                onClick={onCancel}>
                                                {cancelText}
                                            </Button>
                                            <Button
                                                ref={buttonRef}
                                                disabled={!valid || confirmButtonDisabled}
                                                variant={
                                                    type === 'danger' ? 'secondary' : 'primary'
                                                }
                                                size="medium"
                                                className={`justify-center infline-flex block`}
                                                onClick={onConfirm}>
                                                {confirmText}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>
        );
    },
);

Modal.displayName = 'ModalComponent';

function ModalElement({
    children,
    size = 'large',
}: {
    children: React.ReactNode;
    size?: 'large' | 'medium' | 'small';
}) {
    const classNames = cs({
        ['!overflow-visible']: true,
        [s.contents]: true,
        ['max-w-md sm:max-w-lg sm:w-full']: size === 'large',
        ['max-w-lg w-[500px] ']: size === 'medium',
        ['w-full sm:max-w-sm w-[400px] min-h-[300px]']: size === 'small',
    });
    return (
        <div className="fixed inset-0 overflow-y-auto">
            <div className={s.modal}>
                <span
                    className="hidden sm:inline-block sm:align-middle sm:h-screen"
                    aria-hidden="true">
                    &#8203;
                </span>
                <div className={classNames}>{children}</div>
            </div>
        </div>
    );
}

function Header({
    children,
    type,
    className = '',
}: {
    children: React.ReactNode;
    type?: 'success' | 'warning' | 'info' | 'danger' | 'dangerAlert' | 'upgrade';
    className?: string;
}) {
    return (
        <div className={`pt-10 pb-4 ${className}`}>
            {type === 'dangerAlert' ? (
                <DangerAlertSVG className="mx-auto" />
            ) : type && <IconSelector type={type} />}
            <div className="mt-3 text-center sm:mt-5">
                <h3 className="text-2xl leading-6 font-medium text-gray-900">{children}</h3>
            </div>
        </div>
    );
}

function BodyContainer({
    children,
    classNames,
    hideBase,
}: {
    children: React.ReactNode;
    classNames?: string;
    hideBase?: boolean;
}) {
    const rootContentsClass = cs(!hideBase ? s.baseContents : undefined, classNames);
    return <div className={rootContentsClass}>{children}</div>;
}

function ButtonContainer({
    children,
    classNames,
    hideBase,
}: {
    children: React.ReactNode;
    classNames?: string;
    hideBase?: boolean;
}) {
    const rootButtonContents = cs(!hideBase ? s.buttons : undefined, classNames);
    return <div className={rootButtonContents}>{children}</div>;
}

function ConfirmButton({
    disabled,
    type,
    onConfirm,
    children,
    className
}: {
    disabled?: boolean;
    type?: 'success' | 'warning' | 'info' | 'danger' | 'dangerAlert' | 'upgrade';
    onConfirm: () => void;
    children: React.ReactNode;
    className?: string
}) {
    return (
        <Button
            disabled={disabled}
            variant={type === 'danger' ? 'secondary' : 'primary'}
            size="medium"
            className={`justify-center infline-flex block ${className}`}
            onClick={onConfirm}>
            {children}
        </Button>
    );
}

function CancelButton({ onCancel, children }: { onCancel: () => void; children: React.ReactNode }) {
    return (
        <Button
            variant="white"
            size="medium"
            className="justify-center infline-flex block"
            onClick={onCancel}>
            {children}
        </Button>
    );
}

ModalElement.Header = Header;
ModalElement.Body = BodyContainer;
ModalElement.ButtonContainer = ButtonContainer;
ModalElement.CancelButton = CancelButton;
ModalElement.ConfirmButton = ConfirmButton;

export { Modal, ModalElement };
